import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PWAInstallPrompt } from './PWAInstallPrompt';

interface ProtectedRouteProps {
    redirectPath?: string;
    children?: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
    redirectPath = '/login',
    children
}) => {
    const { user, loading, isPlanExpired, role, isFirstAccess } = useAuth();
    const location = useLocation();

    if (loading) {
        // Basic loading state to prevent flash of redirect
        return (
            <div className="flex items-center justify-center min-h-screen bg-bg dark:bg-darkBg">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to={redirectPath} replace />;
    }

    // --- NOVA TRAVA: Primeiro Acesso (Troca de Senha Obrigatória) ---
    // Se o usuário precisa trocar a senha e NÃO está na página de troca de senha
    const isChangePasswordPage = location.pathname.includes('change-password');
    if (isFirstAccess && !isChangePasswordPage) {
        // Redireciona para a página de troca de senha correta baseado no role
        const targetPath = role === 'paciente' ? '/patient/change-password' : '/psych/change-password';
        return <Navigate to={targetPath} replace />;
    }

    if (isPlanExpired) {
        if (role === 'psicologo') {
            // Se estiver tentando acessar algo que não seja a página de planos ou o modo leitura
            if (location.pathname !== '/plans' && location.pathname !== '/psych/read-only') {
                return <Navigate to="/psych/read-only" replace />;
            }
        }
        // Paciente ou Apoio: Let them proceed so the Layout component can display the secure overlay block.
    }

    const showPWA = role && ['psicologo', 'paciente', 'apoio'].includes(role);

    return (
        <>
            {showPWA && <PWAInstallPrompt />}
            {children ? <>{children}</> : <Outlet />}
        </>
    );
};
